// Live persona-chat tool progress via WebSocket.
//
// Backend tool_executor broadcasts chat_tool_invoked / chat_tool_completed /
// chat_tool_timeout / chat_tool_error / chat_budget_exceeded / chat_turn_completed
// to the session's WS channel (/ws/sessions/{sessionId}) whenever a
// budget_envelope is present (i.e. persona chat turns). This hook subscribes,
// tracks the active/last tool, and builds a loadingStatus string that
// ConversationInterface / MessageList already know how to render as
// phase-history pills.
//
// Falls back to an elapsed-based canned ladder when no WS events have arrived
// yet (first ~3s of a turn) — identical to AdminChat's shape so the two UIs
// feel consistent.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useWebSocket } from './useWebSocket';

interface ToolSnapshot {
  tool_call_id: string;
  tool_name: string;
  tool_category?: string;
  llm_round?: number;
  duration_ms?: number;
  status?: 'ok' | 'timeout' | 'error' | 'budget_exceeded';
}

interface PeerSnapshot {
  // Sub-event within a consult_peers fan-out. Surfaces via chat_peer_*
  // WS events broadcast from consult_peers_tool.
  _key: string;  // stable identity for upsert — peer_persona_id OR match_query
  peer_persona_name: string;
  match_query?: string;
  matched_via?: string;
  matched_tag?: string | null;  // specific tag that hit, if any (exact tag match)
  matched_tokens?: string[];    // query tokens that hit (tag_token / sub / name)
  match_score?: number;
  phase: 'resolved' | 'calling' | 'completed' | 'failed';
  // Client-side wall clock (ms since epoch) captured when the peer entered
  // `calling`. Drives the live-ticking "(Ns)" suffix so the UI feels alive
  // during the 30-180s wait instead of sitting on a static label.
  started_at?: number;
  duration_ms?: number;
  total_tokens?: number;
  error?: string;
  status?: string; // offline / timeout / error / budget_exceeded when phase=failed
}

// Structured phase event — mirrors the backend's phase_events sink shape
// so live-collected events and reload-persisted events share the same type
// and the phase-bubble renderer (MessageList / MessageBubble) has one code
// path for both.
export interface PhaseEvent {
  kind: string;                       // "chat_tool_invoked", "chat_peer_call_completed", etc.
  occurred_at_ms: number;             // milliseconds since epoch
  label: string;                      // server-built display string — "✓ Zero (23.4s, 487t)"
  details: Record<string, unknown>;   // event-specific data for click-to-expand
}

export interface UsePersonaToolProgressResult {
  loadingStatus: string;
  phaseEvents: PhaseEvent[];
  activeToolCount: number;
  completedCount: number;
  connectionStatus: 'Connecting' | 'Open' | 'Closed' | 'Error';
}

// Friendly tool labels — fall through to the raw tool_name (underscores
// replaced with spaces) for anything unmapped.
const TOOL_LABEL: Record<string, string> = {
  search: 'Searching',
  consult_peers: 'Consulting peers',
  memory: 'Checking memory',
  thread: 'Reading thread',
  tool_docs: 'Looking up tool docs',
  roster: 'Reviewing roster',
  skills_query: 'Querying skills',
  suggestions_read: 'Reading suggestions',
  attention_items: 'Checking attention items',
  checkpoint: 'Checkpointing',
  dry_run_pulse: 'Dry-running pulse',
  model_selection: 'Selecting model',
  cross_system_query: 'Cross-system query',
  persona_lineage_query: 'Tracing persona lineage',
  phase_strategy_query: 'Querying phase strategy',
  artifactory_manifest_query: 'Querying artifactory',
  event_timeline_query: 'Reading event timeline',
  handoff_note_generate: 'Drafting handoff note',
  self_observation: 'Self-observing',
  blackboard: 'Reading blackboard',
  generate_mermaid_diagram: 'Generating diagram',
  mindmap: 'Building mindmap',
  plantuml: 'Rendering PlantUML',
  geojson: 'Building GeoJSON',
  reflection: 'Reflecting',
  document: 'Reading document',
  create_artifacts: 'Writing artifacts',
  token_wallet: 'Checking token wallet',
};

function friendlyLabel(name: string): string {
  return TOOL_LABEL[name] ?? name.replace(/_/g, ' ');
}

// Peer tracking keyed by _key (peer_persona_id if resolved, match_query
// otherwise). Event for a key already in the list updates that peer's
// phase in place; new keys append. Preserves insertion order so the
// phase trail reflects which peer started first.
function _upsertPeer(
  peers: PeerSnapshot[],
  key: string,
  snap: Omit<PeerSnapshot, '_key'>,
): PeerSnapshot[] {
  const entry: PeerSnapshot = { ...snap, _key: key };
  const idx = peers.findIndex((p) => p._key === key);
  if (idx === -1) {
    return [...peers, entry];
  }
  const next = peers.slice();
  next[idx] = entry;
  return next;
}

function formatDuration(ms: number | undefined): string {
  if (ms == null) return '';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function statusMark(status: ToolSnapshot['status']): string {
  if (status === 'ok') return '✓';
  if (status === 'timeout') return '⏱';
  if (status === 'error') return '✗';
  if (status === 'budget_exceeded') return '⊘';
  return '·';
}

export function usePersonaToolProgress({
  sessionId,
  enabled,
}: {
  sessionId: string | undefined;
  enabled: boolean;
}): UsePersonaToolProgressResult {
  const [currentTool, setCurrentTool] = useState<ToolSnapshot | null>(null);
  const [lastCompleted, setLastCompleted] = useState<ToolSnapshot | null>(null);
  const [activeToolCount, setActiveToolCount] = useState(0);
  const [completedCount, setCompletedCount] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [hasWsEvents, setHasWsEvents] = useState(false);
  // Per-peer progress within a consult_peers fan-out. A Map keyed on
  // peer_persona_id (or match_query for unresolved-offline entries)
  // tracks ALL peers currently in flight — parallel fan-outs of 2-5
  // peers would otherwise oscillate in a single-slot variable and the
  // UI would flicker between them. Array used instead of Map because
  // React state doesn't re-render on Map mutation.
  const [peers, setPeers] = useState<PeerSnapshot[]>([]);
  // Structured phase-event stream. Every WS-handled transition appends
  // one record — the source of truth for the expandable phase trail.
  // Replaces the old MessageList statusHistory (which was derived from
  // the `loadingStatus` string and silently swallowed individual peer
  // terminal transitions when an aggregate line was showing).
  const [phaseEvents, setPhaseEvents] = useState<PhaseEvent[]>([]);
  // Main-agent LLM thinking heartbeat. Stays "active" for 10s after the
  // last chat_turn_heartbeat so the display doesn't blink off between
  // 5s heartbeats. Phase distinguishes initial thinking from synthesis.
  const [llmThinking, setLlmThinking] = useState<{
    phase: string;
    startedAt: number;  // ms since epoch, for live elapsed display
    expiresAt: number;  // ms since epoch; drop if no heartbeat arrives
  } | null>(null);

  // Latest-enabled ref so our ws handler doesn't stale-close on state.
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  // Reset everything on each enabled transition (new turn starting).
  useEffect(() => {
    if (enabled) {
      setCurrentTool(null);
      setLastCompleted(null);
      setActiveToolCount(0);
      setCompletedCount(0);
      setElapsed(0);
      setHasWsEvents(false);
      setPeers([]);
      setLlmThinking(null);
      setPhaseEvents([]);
    }
  }, [enabled]);

  // Tick-driven "now" so the LLM-thinking elapsed display updates every
  // second between heartbeats. Without this, the elapsed number would
  // jump 0→5→10 when heartbeats arrive instead of ticking smoothly.
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    if (!enabled) return;
    const i = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(i);
  }, [enabled]);

  // Elapsed timer — drives the time-based fallback ladder.
  useEffect(() => {
    if (!enabled) return;
    const interval = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(interval);
  }, [enabled]);

  // WS subscription — stable onMessage via useCallback.
  const onMessage = useCallback((event: MessageEvent) => {
    if (!enabledRef.current) return;
    try {
      const msg = JSON.parse(event.data);
      const t = msg?.type;
      const d = msg?.data ?? {};

      // Append to the structured phase-event stream whenever the backend
      // attached a pre-built `phase_label`. Heartbeats dedup on phase
      // (5s ticks on the same phase produce a single trail entry).
      // Every other event type appends unconditionally so parallel peer
      // transitions all survive (the Bug B fix — aggregate views can no
      // longer swallow individual terminal transitions).
      const phaseLabel: string | null =
        typeof d.phase_label === 'string' && d.phase_label.length > 0
          ? d.phase_label
          : null;
      if (phaseLabel && typeof t === 'string') {
        setPhaseEvents((prev) => {
          if (t === 'chat_turn_heartbeat') {
            const last = prev[prev.length - 1];
            if (
              last &&
              last.kind === 'chat_turn_heartbeat' &&
              (last.details as any)?.phase === d.phase
            ) {
              return prev;
            }
          }
          return [
            ...prev,
            {
              kind: t,
              occurred_at_ms: Date.now(),
              label: phaseLabel,
              details: d,
            },
          ];
        });
      }

      if (t === 'chat_tool_invoked') {
        setHasWsEvents(true);
        setCurrentTool({
          tool_call_id: d.tool_call_id,
          tool_name: d.tool_name,
          tool_category: d.tool_category,
          llm_round: d.llm_round,
        });
        setActiveToolCount((c) => c + 1);
        // A tool is running — the LLM is NOT thinking. Clear heartbeat
        // so the UI doesn't show stale "thinking" while a tool executes.
        setLlmThinking(null);
      } else if (
        t === 'chat_tool_completed' ||
        t === 'chat_tool_timeout' ||
        t === 'chat_tool_error'
      ) {
        setHasWsEvents(true);
        const snap: ToolSnapshot = {
          tool_call_id: d.tool_call_id,
          tool_name: d.tool_name,
          duration_ms: d.duration_ms,
          status: d.status,
        };
        setLastCompleted(snap);
        setActiveToolCount((c) => Math.max(0, c - 1));
        setCompletedCount((c) => c + 1);
        setCurrentTool((current) =>
          current?.tool_call_id === d.tool_call_id ? null : current,
        );
      } else if (t === 'chat_budget_exceeded') {
        setHasWsEvents(true);
        setLastCompleted({
          tool_call_id: d.tool_call_id,
          tool_name: d.tool_name,
          status: 'budget_exceeded',
        });
      } else if (t === 'chat_turn_completed') {
        // End of turn — clear all transient per-peer / per-tool state so the
        // latency window between the backend's turn.completed and the POST
        // response delivering enabled=false doesn't flash back to showing
        // the last failed peer (which looks like a duplicate "✗ X timeout"
        // after "Main agent synthesizing").
        setHasWsEvents(true);
        setLlmThinking(null);
        setPeers([]);
        setLastCompleted(null);
        setCurrentTool(null);
      } else if (t === 'chat_turn_heartbeat') {
        // LLM is still thinking. Set or refresh the thinking state with
        // 10s expiration so it stays visible between 5s heartbeats.
        setHasWsEvents(true);
        setLlmThinking((prev) => {
          const now = Date.now();
          // If phase changed OR prev was null, reset startedAt to now - elapsed_s
          // so the displayed timer reflects BACKEND-reported elapsed (authoritative).
          const elapsedMs = (d.elapsed_s || 0) * 1000;
          if (!prev || prev.phase !== d.phase) {
            return {
              phase: d.phase,
              startedAt: now - elapsedMs,
              expiresAt: now + 10_000,
            };
          }
          // Same phase — just refresh the expiry window; keep startedAt.
          return { ...prev, expiresAt: now + 10_000 };
        });
      } else if (t === 'chat_peer_resolved') {
        setHasWsEvents(true);
        const key = String(d.peer_persona_id || d.match_query || '?');
        setPeers((prev) => _upsertPeer(prev, key, {
          peer_persona_name: d.peer_persona_name,
          match_query: d.match_query,
          matched_via: d.matched_via,
          matched_tag: d.matched_tag,
          matched_tokens: d.matched_tokens,
          match_score: d.match_score,
          phase: 'resolved',
        }));
        setLlmThinking(null);
      } else if (t === 'chat_peer_call_started') {
        setHasWsEvents(true);
        const key = String(d.peer_persona_id || d.match_query || '?');
        setPeers((prev) => _upsertPeer(prev, key, {
          peer_persona_name: d.peer_persona_name,
          match_query: d.match_query,
          phase: 'calling',
          // Client wall clock — drives the live "(Ns)" elapsed suffix in
          // loadingStatus so the user sees the counter turning during the
          // 30-180s peer wait.
          started_at: Date.now(),
        }));
        setLlmThinking(null);
      } else if (t === 'chat_peer_call_completed') {
        setHasWsEvents(true);
        const key = String(d.peer_persona_id || d.match_query || '?');
        setPeers((prev) => _upsertPeer(prev, key, {
          peer_persona_name: d.peer_persona_name,
          match_query: d.match_query,
          phase: 'completed',
          duration_ms: d.duration_ms,
          total_tokens: d.total_tokens,
        }));
      } else if (t === 'chat_peer_call_failed') {
        setHasWsEvents(true);
        const key = String(d.peer_persona_id || d.match_query || '?');
        setPeers((prev) => _upsertPeer(prev, key, {
          peer_persona_name: d.peer_persona_name || d.match_query || '?',
          match_query: d.match_query,
          phase: 'failed',
          status: d.status,
          error: d.error,
        }));
      }
    } catch (err) {
      // Bad frame — ignore. This is observability, not correctness.
      // eslint-disable-next-line no-console
      console.debug('[PERSONA_TOOL_PROGRESS] skip frame:', err);
    }
  }, []);

  const protocol =
    typeof window !== 'undefined' && window.location.protocol === 'https:'
      ? 'wss:'
      : 'ws:';
  const host = typeof window !== 'undefined' ? window.location.host : '';
  const wsUrl =
    enabled && sessionId ? `${protocol}//${host}/ws/sessions/${sessionId}` : '';

  const { connectionStatus } = useWebSocket({
    url: wsUrl,
    onMessage,
    disabled: !enabled || !sessionId,
    connectionId: `persona-tool-progress-${sessionId ?? 'none'}`,
    requireAuth: true,
    reconnectAttempts: 3,
    reconnectInterval: 500,
    pingInterval: 15000,
  });

  // Build the loadingStatus string with a layered priority:
  //   0. Active peer sub-events (consult_peers fan-out)
  //   1. Active tool invocation
  //   2. LLM thinking heartbeat (live-elapsed display)
  //   3. Last completed + thinking — show checkmark + duration
  //   4. WS connected but no events yet — generic thinking ping
  //   5. Fallback: time-based ladder
  let loadingStatus: string;
  // Peer phase: prefer showing in-flight peers. If 2+ are calling,
  // summarize rather than oscillate between them.
  const callingPeers = peers.filter((p) => p.phase === 'calling');
  const latestPeer = peers.length > 0 ? peers[peers.length - 1] : null;

  // LLM heartbeat is valid if we have an active entry AND it hasn't expired.
  const heartbeatActive = !!(llmThinking && llmThinking.expiresAt > nowMs);

  if (callingPeers.length >= 2) {
    const names = callingPeers.map((p) => p.peer_persona_name).join(', ');
    const completed = peers.filter((p) => p.phase === 'completed' || p.phase === 'failed').length;
    const total = peers.length;
    // Earliest-started among calling peers drives the aggregate counter —
    // reflects the wait duration for the slowest still-in-flight peer.
    const earliestStart = callingPeers.reduce(
      (min, p) => (p.started_at && p.started_at < min ? p.started_at : min),
      Number.MAX_SAFE_INTEGER,
    );
    const elapsedS =
      earliestStart < Number.MAX_SAFE_INTEGER
        ? Math.max(0, Math.floor((nowMs - earliestStart) / 1000))
        : 0;
    loadingStatus = `⏳ ${callingPeers.length} peers thinking (${names}) — ${completed}/${total} done (${elapsedS}s)...`;
  } else if (callingPeers.length === 1) {
    // One peer currently calling, even if others completed/failed earlier.
    const peer = callingPeers[0];
    const elapsedS = peer.started_at
      ? Math.max(0, Math.floor((nowMs - peer.started_at) / 1000))
      : 0;
    loadingStatus = `⏳ ${peer.peer_persona_name} thinking (${elapsedS}s)...`;
  } else if (latestPeer && !heartbeatActive) {
    const name = latestPeer.peer_persona_name;
    if (latestPeer.phase === 'resolved') {
      // Prefer specific tag > specific token > generic matched_via label.
      let viaHint = '';
      if (latestPeer.matched_via === 'uuid_exact') {
        viaHint = '';
      } else if (latestPeer.matched_tag) {
        viaHint = ` (tag: ${latestPeer.matched_tag})`;
      } else if (latestPeer.matched_tokens && latestPeer.matched_tokens.length > 0) {
        viaHint = ` (matched: ${latestPeer.matched_tokens[0]})`;
      } else if (latestPeer.matched_via) {
        viaHint = ` (${latestPeer.matched_via.replace(/_/g, ' ')})`;
      }
      loadingStatus = `Matched ${name}${viaHint}...`;
    } else if (latestPeer.phase === 'completed') {
      const dur = formatDuration(latestPeer.duration_ms);
      const tokens = latestPeer.total_tokens ? `, ${latestPeer.total_tokens}t` : '';
      const durPart = dur ? ` (${dur}${tokens})` : '';
      loadingStatus = `✓ ${name}${durPart} — continuing...`;
    } else if (latestPeer.phase === 'failed') {
      const status = latestPeer.status || 'failed';
      loadingStatus = `✗ ${name} — ${status}`;
    } else {
      // shouldn't reach — calling covered above
      loadingStatus = `⏳ ${name}...`;
    }
  } else if (currentTool) {
    const label = friendlyLabel(currentTool.tool_name);
    const roundHint =
      currentTool.llm_round && currentTool.llm_round > 1
        ? ` (round ${currentTool.llm_round})`
        : '';
    loadingStatus = `${label}${roundHint}...`;
  } else if (heartbeatActive && llmThinking) {
    // Backend heartbeat says LLM is still thinking. Show live elapsed.
    const elapsedS = Math.max(0, Math.floor((nowMs - llmThinking.startedAt) / 1000));
    const phaseLabel =
      llmThinking.phase === 'initial'
        ? 'Main agent thinking'
        : llmThinking.phase === 'rescue_synthesis'
        ? 'Rescue synthesis'
        : llmThinking.phase.startsWith('round_')
        ? 'Main agent synthesizing'
        : 'Main agent thinking';
    loadingStatus = `⏳ ${phaseLabel} (${elapsedS}s)...`;
  } else if (latestPeer && heartbeatActive) {
    // Rare: a peer is registered but the LLM is also thinking — prefer
    // the heartbeat line since it's the foreground work.
    const elapsedS = llmThinking
      ? Math.max(0, Math.floor((nowMs - llmThinking.startedAt) / 1000))
      : 0;
    loadingStatus = `⏳ Main agent synthesizing (${elapsedS}s)...`;
  } else if (lastCompleted && hasWsEvents) {
    const dur = formatDuration(lastCompleted.duration_ms);
    const mark = statusMark(lastCompleted.status);
    const label = friendlyLabel(lastCompleted.tool_name);
    const durPart = dur ? ` (${dur})` : '';
    loadingStatus = `${mark} ${label}${durPart} — thinking...`;
  } else if (hasWsEvents) {
    loadingStatus = `Thinking... (${elapsed}s)`;
  } else if (elapsed < 3) {
    loadingStatus = 'Thinking...';
  } else if (elapsed < 8) {
    loadingStatus = 'Analyzing your request...';
  } else if (elapsed < 15) {
    loadingStatus = 'Running tools and gathering data...';
  } else if (elapsed < 25) {
    loadingStatus = `Working on it (${elapsed}s)...`;
  } else {
    // Put the elapsed timer at the END so MessageList's phase-strip regex
    // (which strips `(Ns)...` plus surrounding whitespace) doesn't collapse
    // the space between phrases and emit "Still workingmulti-step operation".
    loadingStatus = `Still working on a multi-step operation (${elapsed}s)...`;
  }

  return {
    loadingStatus,
    phaseEvents,
    activeToolCount,
    completedCount,
    connectionStatus: connectionStatus as UsePersonaToolProgressResult['connectionStatus'],
  };
}
